// @flow
import { Grid } from 'semantic-ui-react';
import React, { useContext } from 'react';
import { useFormikContext } from 'formik';
import { If } from '@presentational/If';

import { StyledGridRow } from '../../common-utils/styledComponents';
import {
  getIfCustomDiscountsEnabledORInCustomerView,
  getIfCustomerViewIsEnabled,
  getIfEstimateIsOnTieredPricingRateCard,
} from '../../common-utils/utils';
import { EstimateContext } from '../../contexts/EstimateContext';
import {
  DEAL_TYPE_BACKEND_NAME,
  DEAL_TYPE_NEW_COMMIT,
  IS_ADVANCED_SECURITY_LIVE_IN_CE1,
  IS_CC_STARTER_COMMIT_LIVE_IN_CE1,
  IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
  IS_GATEWAY_ADD_ON_LIVE_IN_CE1,
} from '../../constants';
import { useIsFedRAMPEstimate } from '../../custom-hooks/useIsFedRAMPEstimate';

import { DealStartDateContainer } from './DealStartDateContainer';
import { DealEndDateContainer } from './DealEndDateContainer';
import { DealTypeContainer } from './DealTypeContainer';
import { MarketplaceContainer } from './MarketplaceContainer';
import { PaymentScheduleContainer } from './PaymentScheduleContainer';
import { RateCardsContainer } from './RateCardsContainer';
import { DiscountAppliedContainer } from './DiscountAppliedContainer';
import { SupportTiersContainer } from './SupportTiersContainer';
import { RenewalWarningContainer } from './RenewalWarningContainer';
import { CustomDiscountsEnabledContainer } from './CustomDiscountsEnabledContainer';
import { CommittedCapacityEnabledContainer } from './CommittedCapacityEnabledContainer';
import { CreditsAndBurstsEnabledContainer } from './CreditsAndBurstsEnabledContainer';
import { AdvancedSecurityEnabledContainer } from './AdvancedSecurityEnabledContainer';
import { GatewayAddOnEnabledContainer } from './GatewayAddOnEnabledContainer';
import { EstimateCommitNetNewCustomerContainer } from './UserCommitNetNewCustomerContainer';
import { EstimateCommitMSKTakeoutContainer } from './EstimateCommitMSKTakeoutContainer';
import { CCStarterCommitContainer } from './CCStarterCommitContainer';

export const EstimateCommitDetailsGrid = () => {
  const estimate = useContext(EstimateContext);
  const isCustomerViewEnabled = getIfCustomerViewIsEnabled(estimate?.inputs);
  // Advanced Security, Committed Capacity and the CC Gateway Add On are not supported on FedRAMP
  // estimates.
  const isFedRAMPEstimate = useIsFedRAMPEstimate();
  const { values } = useFormikContext();
  // CC Starter Commit packages only exist for New Commit deals on the tiered rate card.
  const isCCStarterCommitApplicable =
    IS_CC_STARTER_COMMIT_LIVE_IN_CE1 &&
    getIfEstimateIsOnTieredPricingRateCard(values) &&
    values[DEAL_TYPE_BACKEND_NAME] === DEAL_TYPE_NEW_COMMIT;

  return (
    <Grid>
      <StyledGridRow columns={3} paddingTop="1rem">
        <Grid.Column>
          <Grid>
            <Grid.Row columns={2}>
              <Grid.Column>
                <DealStartDateContainer />
              </Grid.Column>
              <Grid.Column>
                <DealEndDateContainer />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <DealTypeContainer />
        </Grid.Column>
        <Grid.Column>
          <MarketplaceContainer />
        </Grid.Column>
      </StyledGridRow>
      <StyledGridRow columns={3} paddingTop="1rem">
        <Grid.Column>
          <PaymentScheduleContainer />
        </Grid.Column>
        <Grid.Column>
          <RateCardsContainer />
        </Grid.Column>
        <Grid.Column
          style={{
            visibility: getIfCustomDiscountsEnabledORInCustomerView(estimate.inputs)
              ? 'hidden'
              : '',
          }}
        >
          <DiscountAppliedContainer />
        </Grid.Column>
      </StyledGridRow>
      <StyledGridRow columns={3} paddingTop="1rem">
        <Grid.Column>
          <SupportTiersContainer />
        </Grid.Column>
        <Grid.Column verticalAlign="middle">
          <RenewalWarningContainer />
        </Grid.Column>
        <Grid.Column verticalAlign="middle" />
      </StyledGridRow>
      <StyledGridRow columns={3} paddingTop="1rem" verticalAlign="bottom">
        {!isCustomerViewEnabled && (
          <Grid.Column>
            <CustomDiscountsEnabledContainer />
          </Grid.Column>
        )}
        <Grid.Column>
          <CreditsAndBurstsEnabledContainer />
        </Grid.Column>
        <If test={IS_ADVANCED_SECURITY_LIVE_IN_CE1 && !isFedRAMPEstimate}>
          <Grid.Column>
            <AdvancedSecurityEnabledContainer />
          </Grid.Column>
        </If>
      </StyledGridRow>
      <StyledGridRow columns={3} paddingTop="1rem">
        <If test={IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1 && !isFedRAMPEstimate}>
          <Grid.Column>
            <CommittedCapacityEnabledContainer />
          </Grid.Column>
        </If>
        <If test={IS_GATEWAY_ADD_ON_LIVE_IN_CE1 && !isFedRAMPEstimate}>
          <Grid.Column>
            <GatewayAddOnEnabledContainer />
          </Grid.Column>
        </If>
      </StyledGridRow>
      <If test={isCCStarterCommitApplicable}>
        <StyledGridRow columns={3} paddingTop="1rem">
          <Grid.Column>
            <CCStarterCommitContainer />
          </Grid.Column>
        </StyledGridRow>
      </If>
      <If test={!isCustomerViewEnabled}>
        <StyledGridRow columns={3} paddingTop="1rem">
          <Grid.Column>
            <EstimateCommitNetNewCustomerContainer />
          </Grid.Column>
          <Grid.Column>
            <EstimateCommitMSKTakeoutContainer />
          </Grid.Column>
        </StyledGridRow>
      </If>
    </Grid>
  );
};
